import type { EmblaCarouselType } from 'embla-carousel'
import EmblaCarousel from 'embla-carousel'

/**
 * Интерфейс доступных направлений прокрутки
 */
interface AvailableDirections {
  /** Доступность прокрутки назад */
  prev: boolean
  /** Доступность прокрутки вперёд */
  next: boolean
}

/**
 * Интерфейс состояния слайдера
 */
interface SliderState {
  /** DOM элемент слайдера */
  el: Element
  /** Экземпляр Embla Carousel */
  slider: EmblaCarouselType
  /** Индекс активного слайда */
  activeSlideIndex: number
  /** Индексы последовательных маленьких карточек */
  continuousSmallCardsIndexes: number[]
  /** Доступные направления прокрутки */
  availableDirections: AvailableDirections
  /** DOM элемент пагинации */
  pagination?: HTMLElement
}

/**
 * Интерфейс аккумулятора для подсчёта слайдов
 */
interface SlideAccumulator {
  /** Есть ли предыдущий маленький слайд */
  havePreviousSmall: boolean
  /** Массив индексов */
  indexes: number[]
}

/**
 * Обработчик изменения доступных направлений прокрутки
 * Обновляет видимость кнопок навигации
 *
 * @param {SliderState} obj - Состояние слайдера
 * @param {AvailableDirections} value - Новые значения доступных направлений
 */
function availableDirectionsChangeHandler(
  obj: SliderState,
  value: AvailableDirections
): void {
  Object.entries(value).forEach(([key, isAvailable]) => {
    const button = obj.el.querySelector<HTMLElement>(
      `[data-slider-button][data-dir="${key}"]`
    )
    if (!button) return

    button.classList.toggle('is-hidden', !isAvailable)
  })
}

/**
 * Обработчик изменения активного индекса слайда
 * Пропускает последовательные маленькие карточки и обновляет кнопки
 *
 * @param {SliderState} obj - Состояние слайдера
 * @param {number} newValue - Новый индекс слайда
 */
function activeSlideIndexChangeHandler(
  obj: SliderState,
  newValue: number
): void {
  if (newValue === obj.activeSlideIndex) {
    return
  }

  const activePaginationItem = obj.pagination?.querySelector<HTMLButtonElement>(
    `[data-slide-idx="${obj.activeSlideIndex}"]`
  )
  if (activePaginationItem) {
    activePaginationItem.classList.remove('is-active')
  }

  const newActivePaginationItem =
    obj.pagination?.querySelector<HTMLButtonElement>(
      `[data-slide-idx="${newValue}"]`
    )
  if (newActivePaginationItem) {
    newActivePaginationItem.classList.add('is-active')
  }

  const goesBackwards = newValue < obj.activeSlideIndex
  obj.activeSlideIndex = newValue

  while (obj.continuousSmallCardsIndexes.includes(obj.activeSlideIndex)) {
    obj.activeSlideIndex += goesBackwards ? -1 : 1
  }

  obj.slider.scrollTo(obj.activeSlideIndex)

  obj.availableDirections = {
    prev: obj.slider.canScrollPrev(),
    next: obj.slider.canScrollNext()
  }

  availableDirectionsChangeHandler(obj, obj.availableDirections)
}

function pageTargetIndex(state: SliderState, delta: number): number {
  const { target, containerRect, slideRects } = state.slider.internalEngine()
  const viewStart = -target.get()
  const viewSize = containerRect.width
  const index = slideRects.findIndex((rect) => {
    const start = rect.left - containerRect.left
    return delta > 0
      ? start + rect.width > viewStart + viewSize + 1
      : start >= viewStart - viewSize - 1
  })

  if (index === -1) return state.activeSlideIndex + delta

  return delta > 0
    ? Math.max(index, state.activeSlideIndex + 1)
    : Math.min(index, state.activeSlideIndex - 1)
}

/**
 * Инициализирует главный слайдер на странице
 * Настраивает Embla Carousel с управлением состоянием и навигацией
 */
export function initSlider(): void {
  const sliderEl = document.querySelector<HTMLElement>('[data-slider]')
  if (!sliderEl) {
    return
  }

  const state = new Proxy<SliderState>(
    {
      el: sliderEl,
      slider: null as unknown as EmblaCarouselType,
      activeSlideIndex: 0,
      continuousSmallCardsIndexes: [],
      availableDirections: {
        prev: false,
        next: true
      }
    },
    {
      set<K extends keyof SliderState>(
        obj: SliderState,
        prop: K,
        value: SliderState[K]
      ): boolean {
        if (prop === 'activeSlideIndex') {
          activeSlideIndexChangeHandler(obj, value as number)
          return true
        }

        if (prop === 'availableDirections') {
          availableDirectionsChangeHandler(obj, value as AvailableDirections)
        }

        obj[prop] = value

        return true
      }
    }
  )

  // eslint-disable-next-line new-cap
  state.slider = EmblaCarousel(sliderEl, {
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
    watchDrag: (_api, event) =>
      !(event.target as HTMLElement).closest('[data-slider-button]')
  })

  state.slider.on('init', () => {
    const slides = state.slider.slideNodes()

    state.continuousSmallCardsIndexes = slides.reduce<SlideAccumulator>(
      (acc, slide, idx) => {
        if (!slide.classList.contains('hero-slider__entry_small')) {
          acc.havePreviousSmall = false
          return acc
        }

        if (acc.havePreviousSmall) {
          acc.indexes.push(idx)
          return acc
        }

        acc.havePreviousSmall = true
        return acc
      },
      { havePreviousSmall: false, indexes: [] }
    ).indexes
  })

  state.slider.on('select', () => {
    state.activeSlideIndex = state.slider.selectedScrollSnap()
  })

  sliderEl.addEventListener('click', (event: MouseEvent) => {
    const target = event.target as HTMLElement
    const button = target.closest<HTMLElement>('[data-slider-button]')
    if (!button) {
      return
    }

    const delta = button.dataset.dir === 'next' ? 1 : -1
    state.activeSlideIndex = pageTargetIndex(state, delta)
  })

  const pagination =
    sliderEl.querySelector<HTMLElement>('[data-pagination]') ||
    sliderEl.parentElement?.querySelector<HTMLElement>('[data-pagination]')

  if (!pagination) {
    return
  }

  state.pagination = pagination
  pagination.addEventListener('click', (event: MouseEvent) => {
    const target = event.target as HTMLElement
    const bullet = target.closest<HTMLElement>('[data-slide-idx]')
    if (!bullet) {
      return
    }

    state.activeSlideIndex = Number(bullet.dataset.slideIdx)
  })
}
